import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { motion } from "framer-motion";
import {
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowRight, Building2, CalendarDays, CheckCircle2, Download, Mail, Phone, ShieldCheck, TrendingUp, User } from "lucide-react";

import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Slider } from "./components/ui/slider";
import { Textarea } from "./components/ui/textarea";
import { analyzeReport, emailReport, fetchMeta, getPdfUrl } from "./services/api";
import {
  buildDefaultAssessment,
  buildDefaultValuation,
  formatCurrency,
  getReadinessBand,
  isClientComplete,
  sanitisePayload,
  STORAGE_KEY,
} from "./utils/dashboard";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const motionProps = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: "easeOut" },
};

const scoreColorMap = {
  success: "border-emerald-500/30 bg-emerald-500/12 text-emerald-200",
  accent: "border-[#C28F5C]/30 bg-[#C28F5C]/12 text-[#C28F5C]",
  warning: "border-orange-500/30 bg-orange-500/12 text-orange-200",
  error: "border-rose-500/30 bg-rose-500/12 text-rose-200",
};

const scoreRingMap = {
  success: "ring-emerald-500/15",
  accent: "ring-[#C28F5C]/15",
  warning: "ring-orange-500/15",
  error: "ring-rose-500/15",
};

const readinessCategoryExamples = {
  "financial-preparedness": "Example: a buyer will feel more comfortable if monthly accounts are up to date, add-backs are documented, and there are no surprises in the numbers.",
  "governance-leadership": "Example: if the owner stepped away for a month, the team should still know who approves key decisions and how the business keeps moving.",
  "market-position-revenue-mix": "Example: buyers usually respond better when revenue comes from a mix of clients and there is a clear reason customers keep coming back.",
  "operational-efficiency-scalability": "Example: if a new manager joined tomorrow, there should be clear systems, handover notes, and practical KPIs to follow.",
  "legal-compliance": "Example: licences, contracts, and ownership documents should be organised and ready so a buyer does not uncover avoidable risks later.",
  "strategic-vision-growth-potential": "Example: a short written growth plan with a few realistic targets can help a buyer see where the next upside will come from.",
  "risk-management-contingencies": "Example: the business should be able to keep operating even if a key person is away, a system fails, or a disruption hits.",
  "owner-personal-readiness": "Example: owners who know their target outcome and what life looks like after a sale usually make better decisions during the exit process.",
};

const readinessWhyThisMatters = {
  "clean-financial-statements": "Why this matters: if the numbers are hard to trust, buyers become cautious very quickly and value can drop.",
  "professionalised-management": "Why this matters: the more the business depends on the owner, the harder it is for a buyer to picture a smooth handover.",
  "recurring-revenue": "Why this matters: dependable income often gives buyers more confidence about future cash flow and stability.",
  "customer-concentration": "Why this matters: too much reliance on one customer can make the whole business feel fragile to a buyer.",
  "documented-processes-kpis": "Why this matters: clear systems make the business feel easier to transfer, run, and grow after settlement.",
  "regulatory-compliance": "Why this matters: unresolved compliance issues can slow deals down or create price pressure late in the process.",
  "financial-independence": "Why this matters: when the owner knows the personal number they need, the exit plan becomes much clearer and more grounded.",
};

function ScoreBadge({ score, bands, testId }) {
  const band = getReadinessBand(bands, score);
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
        scoreColorMap[band?.color] || scoreColorMap.error
      )}
      data-testid={testId}
    >
      {score.toFixed(1)} • {band?.grade}
    </span>
  );
}

function PageHeader({ eyebrow, title, description, testId }) {
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">{eyebrow}</p>
      <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
      <p className="editorial-copy max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">{description}</p>
      <div className="h-px w-24 bg-[#C28F5C]/35" />
    </div>
  );
}

function SectionCard({ children, className = "", testId }) {
  return (
    <div
      className={cx(
        "section-fade relative min-w-0 overflow-hidden rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.07)] md:p-8",
        className
      )}
      data-testid={testId}
    >
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function ChartSurface({ children, height, testId }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const updateWidth = () => {
      if (!containerRef.current) {
        return;
      }
      const nextWidth = Math.floor(containerRef.current.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setWidth(nextWidth);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full" data-testid={testId} style={{ height }}>
      {width > 40 ? children(width, height) : null}
    </div>
  );
}

function MetricTile({ label, value, hint, testId }) {
  return (
    <div className="metric-card rounded-3xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <p className="text-sm font-medium uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-3 text-3xl font-extrabold text-slate-900">{value}</p>
      <p className="mt-2 text-sm text-slate-500">{hint}</p>
    </div>
  );
}

function ConfidencePrompt({ message, testId }) {
  return (
    <div className="rounded-[22px] border border-[#C28F5C]/20 bg-[#C28F5C]/10 px-4 py-3" data-testid={testId}>
      <p className="text-sm leading-relaxed text-[#C28F5C]">{message}</p>
    </div>
  );
}

function ClientAtAGlance({ client }) {
  const items = [
    { icon: User, label: "Client", value: client.client_name || "Not added yet" },
    { icon: Building2, label: "Business", value: client.business_name || "Not added yet" },
    { icon: Phone, label: "Telephone", value: client.client_phone || "Not added yet" },
    { icon: Mail, label: "Email", value: client.client_email || "Not added yet" },
    { icon: CalendarDays, label: "Exit horizon", value: `${client.planned_exit_years || 3} year(s)` },
  ];

  return (
    <SectionCard testId="dashboard-client-at-a-glance-strip">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Client at a glance</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900">Contact details for live discussions</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {items.map(({ icon: Icon, label, value }) => (
            <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4" data-testid={`dashboard-at-a-glance-${label.toLowerCase().replace(/\s+/g, "-")}`} key={label}>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#C28F5C]/15 text-[#C28F5C]">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
                  <p className="mt-2 break-words text-sm font-semibold leading-relaxed text-slate-900">{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}

function StepNav() {
  const links = [
    { to: "/", label: "Overview" },
    { to: "/assessment", label: "Exit readiness" },
    { to: "/valuation", label: "Valuation" },
    { to: "/dashboard", label: "Dashboard" },
  ];

  return (
    <nav className="flex flex-wrap gap-3" data-testid="step-navigation">
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cx(
              "rounded-full border px-4 py-2 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5",
              isActive
                ? "border-[#C28F5C]/35 bg-[#C28F5C]/15 text-[#C28F5C]"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            )
          }
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Shell({ meta, children, client }) {
  return (
    <div className="dark-brand min-h-screen text-slate-100" data-testid="care-outcomes-app-shell">
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-slate-950/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4" data-testid="brand-header">
            <img
              alt="CARE Outcomes"
              className="h-10 w-auto rounded-sm"
              data-testid="care-outcomes-logo"
              src={meta.brand.logo_url}
            />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Client Dashboard</p>
              <p className="text-base font-semibold text-slate-900" data-testid="client-business-name-summary">
                {client.business_name || "Business Exit & Valuation Scoreboard"}
              </p>
            </div>
          </div>
          <StepNav />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">{children}</main>

      <footer className="mt-10 border-t border-slate-800 bg-slate-950 text-slate-100">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 sm:px-6 lg:px-8 md:flex-row md:items-center md:justify-between">
          <div data-testid="app-footer-details">
            <p className="text-lg font-semibold">{meta.brand.name}</p>
            <p className="text-sm text-slate-300">{meta.brand.footer_text}</p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <a className="hover:text-white" data-testid="footer-website-link" href={meta.brand.website} rel="noreferrer" target="_blank">
              {meta.brand.website}
            </a>
            <a className="hover:text-white" data-testid="footer-email-link" href={`mailto:${meta.brand.contact_email}`}>
              {meta.brand.contact_email}
            </a>
            <a className="hover:text-white" data-testid="footer-linkedin-link" href={meta.brand.linkedin} rel="noreferrer" target="_blank">
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ClientDetailsPanel({ client, updateClient }) {
  return (
    <SectionCard testId="client-details-panel">
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Client details</p>
          <h2 className="mt-2 text-2xl font-bold text-slate-900">Personalise the report output</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 xl:gap-4">
          {[
            ["client_name", "Client name", "client-name-input", "e.g. Alex Carter"],
            ["client_email", "Client email", "client-email-input", "alex@company.com"],
            ["client_phone", "Contact telephone number", "client-phone-input", "e.g. +61 4XX XXX XXX"],
            ["business_name", "Business name", "business-name-input", "Acme Health Services"],
          ].map(([field, label, testId, placeholder]) => (
            <label className="min-w-0 space-y-2" key={field}>
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <Input
                className="h-11 w-full rounded-2xl border-slate-200 bg-white"
                data-testid={testId}
                onChange={(event) => updateClient(field, event.target.value)}
                placeholder={placeholder}
                type={field === "client_email" ? "email" : field === "client_phone" ? "tel" : "text"}
                value={client[field]}
              />
            </label>
          ))}
          <label className="min-w-0 space-y-2">
            <span className="text-sm font-medium text-slate-700">Planned exit horizon</span>
            <Input
              className="h-11 w-full rounded-2xl border-slate-200 bg-white"
              data-testid="planned-exit-years-input"
              min="1"
              max="15"
              onChange={(event) => updateClient("planned_exit_years", event.target.value)}
              type="number"
              value={client.planned_exit_years}
            />
          </label>
        </div>
      </div>
    </SectionCard>
  );
}

function LandingPage({ meta, client, updateClient }) {
  return (
    <motion.div className="space-y-8" {...motionProps}>
      <section className="hero-shell overflow-hidden rounded-[36px] p-6 text-white shadow-[0_28px_90px_rgba(15,23,42,0.18)] md:p-10" data-testid="landing-hero-section">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <div className="space-y-6">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#C28F5C]">CARE Outcomes</p>
            <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Helping business owners achieve their full sale potential.
            </h1>
            <p className="editorial-copy max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg" data-testid="landing-hero-copy">
              This client-facing scoreboard blends exit readiness and valuation into one practical dashboard, so you can understand value today and the actions that can improve transferability before a sale.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="h-12 rounded-full bg-[#C28F5C] px-6 text-sm font-semibold text-black hover:-translate-y-0.5 hover:bg-[#d3a374]" data-testid="landing-start-readiness-button">
                <Link to="/assessment">Start exit readiness</Link>
              </Button>
              <Button asChild className="h-12 rounded-full border border-[#C28F5C]/35 bg-transparent px-6 text-sm font-semibold text-[#C28F5C] hover:-translate-y-0.5 hover:bg-[#C28F5C]/10" data-testid="landing-go-to-valuation-button" variant="ghost">
                <Link to="/valuation">Jump to valuation</Link>
              </Button>
            </div>
          </div>

          <div className="glass-panel rounded-[28px] p-6" data-testid="landing-highlight-panel">
            <div className="grid gap-4 sm:grid-cols-2">
              <MetricTile hint="Weighted readiness score" label="Exit grade" testId="landing-exit-grade-card" value="1–10" />
              <MetricTile hint="Low, midpoint, and upside range" label="Value range" testId="landing-value-range-card" value="AUD" />
              <MetricTile hint="Quarterly or annual progress reviews" label="Tracking" testId="landing-tracking-card" value="Ongoing" />
              <MetricTile hint={meta.email_enabled ? "PDF and email delivery available" : "Email flow is ready for SMTP connection"} label="Reports" testId="landing-reports-card" value="PDF" />
            </div>
          </div>
        </div>
      </section>

      <ClientDetailsPanel client={client} updateClient={updateClient} />

      <div className="grid gap-6 lg:grid-cols-3">
        {[
          [ShieldCheck, "Exit readiness scorecard", "Measure financial health, owner dependence, governance, systems, legal readiness, and personal preparedness."],
          [TrendingUp, "Indicative valuation range", "Compare sales, SDE, EBITDA, and asset-based methods with qualitative adjustments and sensitivity."],
          [CheckCircle2, "Action-led insights", "Highlight the biggest value gaps, recommend practical actions, and frame an improvement timeline for the next 1–3 years."],
        ].map(([Icon, title, copy], index) => (
          <SectionCard className="warm-grid" key={title} testId={`landing-feature-card-${index + 1}`}>
            <div className="space-y-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C28F5C]/15 text-[#C28F5C]">
                <Icon className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
              <p className="text-base leading-relaxed text-slate-600">{copy}</p>
            </div>
          </SectionCard>
        ))}
      </div>
    </motion.div>
  );
}

function AssessmentPage({ assessment, bands, updateWeight, updateQuestionScore, updateQuestionExplanation }) {
  const navigate = useNavigate();

  return (
    <motion.div className="space-y-8" {...motionProps}>
      <PageHeader
        description="Score every sub-category from 1 to 10, adjust the weighting to fit the business context, and capture notes for any weak area so the action plan is useful."
        eyebrow="Step 1"
        testId="assessment-page-header"
        title="Exit readiness assessment"
      />
      <ConfidencePrompt
        message="You do not need perfect answers here — honest scores usually create the most useful plan."
        testId="assessment-confidence-prompt"
      />

      <SectionCard testId="assessment-guidance-card">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
          <div>
            <p className="text-base leading-relaxed text-slate-600">
              Use 1 for very weak and 10 for excellent. Categories below 7 will be flagged as gaps, and your weight changes will automatically reshape the overall score.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {bands.map((band) => (
              <div
                className={cx("rounded-2xl border px-4 py-3 text-sm font-semibold", scoreColorMap[band.color])}
                data-testid={`readiness-band-${band.grade.toLowerCase()}`}
                key={band.grade}
              >
                {band.grade}: {band.label}
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      <div className="space-y-6">
        {assessment.categories.map((category, categoryIndex) => {
          const average =
            category.questions.reduce((sum, question) => sum + Number(question.score || 0), 0) /
            category.questions.length;

          return (
            <SectionCard key={category.slug} testId={`assessment-category-card-${category.slug}`}>
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-2xl font-bold text-slate-900">{category.title}</h2>
                      <ScoreBadge bands={bands} score={average} testId={`assessment-category-score-${category.slug}`} />
                    </div>
                    <p className="max-w-3xl text-base leading-relaxed text-slate-600">{category.rationale}</p>
                    <p className="text-sm leading-relaxed text-slate-500" data-testid={`assessment-category-example-${category.slug}`}>
                      {readinessCategoryExamples[category.slug]}
                    </p>
                  </div>
                  <label className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:w-60">
                    <span className="text-sm font-medium text-slate-700">Category weight (%)</span>
                    <Input
                      className="h-11 rounded-2xl border-slate-200 bg-white"
                      data-testid={`assessment-weight-input-${category.slug}`}
                      min="1"
                      onChange={(event) => updateWeight(categoryIndex, event.target.value)}
                      type="number"
                      value={category.weight}
                    />
                  </label>
                </div>

                <div className="space-y-5">
                  {category.questions.map((question, questionIndex) => (
                    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5" key={question.slug}>
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="max-w-3xl space-y-2">
                          <p className="text-lg font-semibold text-slate-900">{question.title}</p>
                          <p className="text-sm leading-relaxed text-slate-600">{question.prompt}</p>
                          {readinessWhyThisMatters[question.slug] ? (
                            <p className="rounded-2xl border border-[#C28F5C]/20 bg-[#C28F5C]/10 px-3 py-2 text-xs font-medium leading-relaxed text-[#C28F5C]" data-testid={`assessment-why-this-matters-${question.slug}`}>
                              {readinessWhyThisMatters[question.slug]}
                            </p>
                          ) : null}
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Low: {question.low_hint}</p>
                          <p className="text-xs uppercase tracking-[0.18em] text-emerald-500">High: {question.high_hint}</p>
                        </div>
                        <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200" data-testid={`assessment-score-pill-${category.slug}-${question.slug}`}>
                          Score: {question.score}
                        </div>
                      </div>

                      <div className="mt-5 space-y-4">
                        <Slider
                          className="py-3"
                          data-testid={`assessment-slider-${category.slug}-${question.slug}`}
                          max={10}
                          min={1}
                          onValueChange={(value) => updateQuestionScore(categoryIndex, questionIndex, value[0])}
                          step={1}
                          value={[Number(question.score)]}
                        />

                        <label className="block space-y-2">
                          <span className="text-sm font-medium text-slate-700">Why is this score what it is?</span>
                          <Textarea
                            className="min-h-[92px] rounded-2xl border-slate-200 bg-white"
                            data-testid={`assessment-explanation-${category.slug}-${question.slug}`}
                            onChange={(event) => updateQuestionExplanation(categoryIndex, questionIndex, event.target.value)}
                            placeholder="Add context, gaps, or evidence that should appear in the action plan."
                            value={question.explanation}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button className="h-12 rounded-full bg-slate-900 px-6 text-white hover:-translate-y-0.5" data-testid="assessment-next-button" onClick={() => navigate("/valuation")}>
          Continue to valuation <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}

function ValuationPage({ meta, valuation, updateValuationField, updateOverride, updateQualitativeScore }) {
  const navigate = useNavigate();
  const benchmark = meta.industry_benchmarks.find((item) => item.slug === valuation.industry_slug) || meta.industry_benchmarks[0];
  const fieldExamples = {
    gross_revenue: "Example: use total sales from your last 12 months of profit and loss statements.",
    sde: "Example: start with profit, then add owner wages or drawings that a buyer may adjust for.",
    ebitda: "Example: use earnings before interest, tax, depreciation, and amortisation if you already track it.",
    net_assets: "Example: think about equipment, stock, and other business assets after subtracting liabilities.",
  };

  return (
    <motion.div className="space-y-8" {...motionProps}>
      <PageHeader
        description="Use this section to build a practical value range for the business. Start with the typical industry settings, then adjust them if local comparable sales or business quality factors suggest a different position."
        eyebrow="Step 2"
        testId="valuation-page-header"
        title="Business valuation inputs"
      />
      <ConfidencePrompt
        message="Use the best numbers you have today — this is a planning guide, not a test of precision."
        testId="valuation-confidence-prompt"
      />

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <SectionCard testId="valuation-core-inputs-card">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 md:col-span-2">
              <span className="text-sm font-medium text-slate-700">Industry benchmark</span>
              <select
                className="flex h-11 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm shadow-sm outline-none ring-0 focus:border-amber-400"
                data-testid="valuation-industry-select"
                onChange={(event) => updateValuationField("industry_slug", event.target.value)}
                value={valuation.industry_slug}
              >
                {meta.industry_benchmarks.map((industry) => (
                  <option key={industry.slug} value={industry.slug}>
                    {industry.label}
                  </option>
                ))}
              </select>
              <p className="text-xs leading-relaxed text-slate-500" data-testid="valuation-industry-example-text">
                Example: choose the closest industry to the business so the starting value range reflects a more relevant market view.
              </p>
            </label>

            {[
              ["gross_revenue", "Gross revenue (last 12 months)", "valuation-gross-revenue-input"],
              ["sde", "Profit + owner pay (SDE)", "valuation-sde-input"],
              ["ebitda", "EBITDA (for manager-run businesses)", "valuation-ebitda-input"],
              ["net_assets", "Net assets (asset value floor)", "valuation-net-assets-input"],
            ].map(([field, label, testId]) => (
              <label className="min-w-0 space-y-2" key={field}>
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <Input
                  className="h-11 w-full rounded-2xl border-slate-200 bg-white"
                  data-testid={testId}
                  onChange={(event) => updateValuationField(field, event.target.value)}
                  placeholder="0"
                  type="number"
                  value={valuation[field]}
                />
                <p className="text-xs leading-relaxed text-slate-500" data-testid={`valuation-${field}-example-text`}>
                  {fieldExamples[field]}
                </p>
              </label>
            ))}
          </div>

          <div className="mt-6 rounded-[24px] border border-amber-200 bg-amber-50 p-5" data-testid="valuation-industry-benchmark-panel">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-700">Typical starting point</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <MetricTile hint="A simple revenue-based view" label="Revenue view" testId="valuation-default-sales-multiple" value={`${benchmark.sales_multiple}x`} />
              <MetricTile hint="Based on profit plus owner pay" label="Owner earnings view" testId="valuation-default-sde-multiple" value={`${benchmark.sde_multiple}x`} />
              <MetricTile hint="Useful where management is less owner-dependent" label="Business earnings view" testId="valuation-default-ebitda-multiple" value={`${benchmark.ebitda_multiple}x`} />
            </div>
          </div>
        </SectionCard>

        <SectionCard className="bg-slate-900 text-white" testId="valuation-overrides-card">
          <div className="space-y-5">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-200">Override multiples</p>
              <h2 className="mt-2 text-2xl font-bold">Refine for local comps</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">Use this if recent comparable sales or adviser judgement suggest a stronger or softer starting point.</p>
            </div>

            {[
              ["sales_multiple", "Sales multiple", "valuation-override-sales-input"],
              ["sde_multiple", "SDE multiple", "valuation-override-sde-input"],
              ["ebitda_multiple", "EBITDA multiple", "valuation-override-ebitda-input"],
            ].map(([field, label, testId]) => (
              <label className="min-w-0 space-y-2" key={field}>
                <span className="text-sm font-medium text-slate-200">{label}</span>
                <Input
                  className="h-11 w-full rounded-2xl border-slate-700 bg-slate-950 text-white"
                  data-testid={testId}
                  onChange={(event) => updateOverride(field, event.target.value)}
                  placeholder="Leave blank to use default"
                  type="number"
                  value={valuation.multiple_overrides[field]}
                />
                <p className="text-xs leading-relaxed text-slate-400" data-testid={`valuation-${field}-override-example-text`}>
                  Example: only adjust this if you have a strong reason, such as recent comparable sales or adviser judgement.
                </p>
              </label>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard testId="valuation-qualitative-factors-card">
        <div className="space-y-6">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Transferability adjustments</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Business qualities that raise or lower value</h2>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {meta.qualitative_factors.map((factor) => (
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5" key={factor.slug}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{factor.label}</p>
                    <p className="mt-1 text-sm text-slate-600">{factor.prompt}</p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500" data-testid={`qualitative-example-${factor.slug}`}>
                      Example: score this higher when the business feels easier for a buyer to take over with confidence.
                    </p>
                  </div>
                  <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200" data-testid={`qualitative-score-pill-${factor.slug}`}>
                    {valuation.qualitative_scores[factor.slug]}/5
                  </div>
                </div>
                <Slider
                  className="mt-5 py-3"
                  data-testid={`qualitative-slider-${factor.slug}`}
                  max={5}
                  min={1}
                  onValueChange={(value) => updateQualitativeScore(factor.slug, value[0])}
                  step={1}
                  value={[Number(valuation.qualitative_scores[factor.slug]) || 3]}
                />
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      <div className="flex flex-wrap justify-end gap-3">
        <Button className="h-12 rounded-full border border-slate-300 bg-white px-6 text-slate-700 hover:-translate-y-0.5" data-testid="valuation-back-button" onClick={() => navigate("/assessment")} variant="outline">
          Back to assessment
        </Button>
        <Button className="h-12 rounded-full bg-slate-900 px-6 text-white hover:-translate-y-0.5" data-testid="valuation-dashboard-button" onClick={() => navigate("/dashboard")}>
          Review dashboard <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}

function DashboardPage({ meta, client, report, generateReport, generating, sendEmailFlow, emailing }) {
  const reportReady = Boolean(report?.report_id);
  const readinessData = report?.results?.readiness?.category_summaries || [];
  const valuationMethods = (report?.results?.valuation?.methods || []).filter((item) => item.valuation !== null);
  const currentMidpoint = report?.results?.valuation?.range?.mid || 0;
  const improvedMidpoint = report?.results?.valuation?.range?.target_mid_if_gaps_closed || currentMidpoint;
  const valuationUplift = report?.results?.valuation?.range?.uplift || 0;
  const strongestArea = report?.results?.readiness?.strongest_categories?.[0];
  const mainGap = report?.results?.readiness?.critical_gaps?.[0];
  const highestMethod = valuationMethods.reduce(
    (best, item) => (!best || (item.valuation || 0) > (best.valuation || 0) ? item : best),
    null
  );
  const sensitivityValues = report?.results?.valuation?.sensitivity?.map((item) => item.value) || [];
  const sensitivitySpread = sensitivityValues.length
    ? Math.max(...sensitivityValues) - Math.min(...sensitivityValues)
    : 0;

  return (
    <motion.div className="space-y-8" {...motionProps}>
      <PageHeader
        description="Bring the readiness score and indicative value range together in one branded view, then download or email a report for follow-up conversations."
        eyebrow="Step 3"
        testId="dashboard-page-header"
        title="Integrated exit and valuation dashboard"
      />
      <ConfidencePrompt
        message="This view is here to guide the next conversation — it is meant to clarify priorities, not to judge the business."
        testId="dashboard-confidence-prompt"
      />

      <ClientAtAGlance client={client} />

      <SectionCard testId="dashboard-actions-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Report actions</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Generate a client-ready snapshot</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600" data-testid="dashboard-action-copy">
              {reportReady
                ? `Latest report prepared for ${client.business_name} on ${report.created_at_display}.`
                : "Generate the dashboard to calculate the weighted readiness score, valuation range, and action plan."}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button
              className="h-12 min-w-[180px] justify-center gap-2 rounded-full bg-[#C28F5C] px-5 text-black hover:-translate-y-0.5 hover:bg-[#d3a374] sm:px-6"
              data-testid="dashboard-generate-report-button"
              disabled={generating}
              onClick={generateReport}
            >
              {generating ? "Generating…" : reportReady ? "Refresh dashboard" : "Generate dashboard"}
            </Button>
            <Button
              className="h-12 min-w-[180px] justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 text-slate-700 hover:-translate-y-0.5 sm:px-6"
              data-testid="dashboard-download-pdf-button"
              disabled={!reportReady}
              onClick={() => window.open(getPdfUrl(report.report_id), "_blank", "noopener,noreferrer")}
              variant="outline"
            >
              <Download className="h-4 w-4" /> Download PDF
            </Button>
            <Button
              className="h-12 min-w-[180px] justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 text-slate-700 hover:-translate-y-0.5 sm:px-6"
              data-testid="dashboard-email-report-button"
              disabled={!reportReady || emailing}
              onClick={sendEmailFlow}
              variant="outline"
            >
              <Mail className="h-4 w-4" /> {emailing ? "Sending…" : "Email my PDF"}
            </Button>
          </div>
        </div>
      </SectionCard>

      {!reportReady ? (
        <SectionCard testId="dashboard-empty-state">
          <div className="grid gap-6 lg:grid-cols-3">
            <MetricTile hint="Every category uses a 1–10 scale" label="Readiness" testId="dashboard-empty-readiness" value="Weighted" />
            <MetricTile hint="Multiple methods create a defensible range" label="Valuation" testId="dashboard-empty-valuation" value="Range" />
            <MetricTile hint={meta.email_enabled ? "Client email delivery available" : "Email flow will activate after SMTP setup"} label="Delivery" testId="dashboard-empty-delivery" value="PDF" />
          </div>
        </SectionCard>
      ) : (
        <>
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-4">
            <MetricTile hint={report.results.readiness.grade_label} label="Overall readiness" testId="dashboard-overall-readiness-card" value={`${report.results.readiness.overall_score}/10`} />
            <MetricTile hint="Illustrative midpoint valuation" label="Midpoint value" testId="dashboard-midpoint-card" value={formatCurrency(report.results.valuation.range.mid)} />
            <MetricTile hint="Potential uplift if key gaps close" label="Potential upside" testId="dashboard-upside-card" value={formatCurrency(report.results.valuation.range.uplift)} />
            <MetricTile hint={report.results.integrated_insights.recommended_review_frequency} label="Review cadence" testId="dashboard-review-cadence-card" value={report.results.integrated_insights.recommended_review_frequency} />
          </div>

          <SectionCard testId="dashboard-side-by-side-valuation-panel">
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Value scenario comparison</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Current vs improved valuation midpoint</h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600" data-testid="dashboard-side-by-side-summary">
                  This side-by-side view shows where the midpoint valuation sits today and where it could move if the priority readiness gaps are closed.
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1fr_1fr_0.8fr]">
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5" data-testid="dashboard-current-valuation-card">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Current midpoint</p>
                  <p className="mt-4 text-4xl font-extrabold text-slate-900">{formatCurrency(currentMidpoint)}</p>
                  <p className="mt-3 text-sm text-slate-600">Based on the business as assessed today.</p>
                </div>
                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 sm:p-5" data-testid="dashboard-improved-valuation-card">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Improved midpoint</p>
                  <p className="mt-4 text-4xl font-extrabold text-slate-900">{formatCurrency(improvedMidpoint)}</p>
                  <p className="mt-3 text-sm text-slate-600">Illustrative scenario after closing the highest-priority gaps.</p>
                </div>
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5" data-testid="dashboard-valuation-gap-card">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Potential uplift</p>
                  <p className="mt-4 text-4xl font-extrabold text-slate-900">{formatCurrency(valuationUplift)}</p>
                  <p className="mt-3 text-sm text-slate-600">A practical way to show the advisory opportunity to clients.</p>
                </div>
              </div>
            </div>
          </SectionCard>

          <div className="grid gap-4 sm:gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <SectionCard testId="dashboard-radar-chart-card">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Exit profile</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">How readiness looks across the business</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="dashboard-readiness-chart-intro">
                    This chart helps you see where the business already feels stronger and where more preparation may make the biggest difference.
                  </p>
                </div>
                <ChartSurface height={360} testId="dashboard-readiness-radar-chart">
                  {(width, height) => (
                    <RadarChart data={readinessData} height={height} outerRadius={Math.min(width, height) * 0.28} width={width}>
                      <PolarGrid stroke="#cbd5e1" />
                      <PolarAngleAxis dataKey="title" tick={{ fill: "#475569", fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                      <Radar dataKey="average_score" fill="#C28F5C" fillOpacity={0.35} name="Readiness" stroke="#C28F5C" strokeWidth={2} />
                      <Tooltip />
                    </RadarChart>
                  )}
                </ChartSurface>
                <p className="text-sm leading-relaxed text-slate-600" data-testid="dashboard-readiness-chart-takeaway">
                  {strongestArea && mainGap
                    ? `Takeaway: ${strongestArea.title} looks like an encouraging strength, while ${mainGap.title.toLowerCase()} is the clearest place to focus next.`
                    : "Takeaway: this view is here to show where the business feels strongest and where earlier preparation may have the biggest impact."}
                </p>
              </div>
            </SectionCard>

            <SectionCard testId="dashboard-valuation-chart-card">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Value range</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Value comparison</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="dashboard-valuation-chart-intro">
                    This view shows the different ways value can be looked at, so you can see the range rather than feeling tied to one single number.
                  </p>
                </div>
                <ChartSurface height={360} testId="dashboard-valuation-bar-chart">
                  {(width, height) => (
                    <BarChart data={valuationMethods} height={height} margin={{ left: 12, right: 12, top: 8, bottom: 8 }} width={width}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#475569", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="valuation" fill="#C28F5C" radius={[10, 10, 0, 0]} />
                    </BarChart>
                  )}
                </ChartSurface>
                <p className="text-sm leading-relaxed text-slate-600" data-testid="dashboard-valuation-chart-takeaway">
                  {highestMethod
                    ? `Takeaway: the ${highestMethod.label.toLowerCase()} is currently showing the strongest value indication, which helps frame the upper end of the conversation.`
                    : "Takeaway: this comparison is meant to show a sensible range, not lock you into one single number."}
                </p>
              </div>
            </SectionCard>
          </div>

          <div className="grid gap-4 sm:gap-6 xl:grid-cols-[0.95fr_1.05fr]">
            <SectionCard testId="dashboard-summary-card">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-2xl font-bold text-slate-900">What this is gently pointing to</h2>
                  <span className={cx("rounded-full px-3 py-1 text-xs font-semibold ring-4", scoreColorMap[report.results.readiness.band_color], scoreRingMap[report.results.readiness.band_color])} data-testid="dashboard-grade-pill">
                    Grade {report.results.readiness.grade}
                  </span>
                </div>
                <p className="text-base leading-relaxed text-slate-600" data-testid="dashboard-headline-insight">
                  {report.results.integrated_insights.headline}
                </p>
                <p className="text-base leading-relaxed text-slate-600" data-testid="dashboard-value-gap-summary">
                  {report.results.integrated_insights.value_gap_summary}
                </p>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Main improvement opportunities</p>
                  <div className="mt-4 space-y-3">
                    {report.results.readiness.critical_gaps.slice(0, 4).map((gap) => (
                      <div className="flex flex-col gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-slate-200 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-testid={`dashboard-gap-${gap.slug}`} key={gap.slug}>
                        <div>
                          <p className="font-semibold text-slate-900">{gap.title}</p>
                          <p className="text-sm text-slate-500">{gap.commentary}</p>
                        </div>
                        <ScoreBadge bands={meta.readiness_bands} score={gap.average_score} testId={`dashboard-gap-score-${gap.slug}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard testId="dashboard-action-plan-card">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Suggested next steps</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Practical actions to strengthen sale readiness</h2>
                </div>
                <div className="space-y-4">
                  {report.results.readiness.action_plan.map((action) => (
                    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5" data-testid={`dashboard-action-item-${action.priority}`} key={action.priority}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-lg font-semibold text-slate-900">
                          {action.priority}. {action.category}
                        </p>
                        <span className="rounded-full bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 ring-1 ring-slate-200" data-testid={`dashboard-action-timeframe-${action.priority}`}>
                          {action.timeframe}
                        </span>
                      </div>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">{action.recommended_action}</p>
                      <p className="mt-2 text-sm text-slate-500">Focus: {action.focus}</p>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="grid gap-4 sm:gap-6 xl:grid-cols-[0.95fr_1.05fr]">
            <SectionCard testId="dashboard-sensitivity-card">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Sensitivity</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">How multiple changes affect SDE value</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="dashboard-sensitivity-chart-intro">
                    This chart shows how value can move up or down as buyer confidence changes, which is helpful when thinking about future improvement work.
                  </p>
                </div>
                <ChartSurface height={280} testId="dashboard-sensitivity-chart">
                  {(width, height) => (
                    <BarChart data={report.results.valuation.sensitivity} height={height} width={width}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#475569", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#C28F5C" radius={[10, 10, 0, 0]} />
                    </BarChart>
                  )}
                </ChartSurface>
                <p className="text-sm leading-relaxed text-slate-600" data-testid="dashboard-sensitivity-chart-takeaway">
                  {sensitivitySpread > 0
                    ? `Takeaway: even modest shifts in buyer confidence could move value by about ${formatCurrency(sensitivitySpread)}, which is why readiness improvements matter.`
                    : "Takeaway: this view helps show how confidence in the business can change value, even when the numbers stay similar."}
                </p>
              </div>
            </SectionCard>

            <SectionCard testId="dashboard-timeline-card">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Planning timeline</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">A gentle view of what to work on next</h2>
                </div>
                <div className="space-y-4">
                  {report.results.readiness.timeline.map((item, index) => (
                    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5" data-testid={`dashboard-timeline-item-${index + 1}`} key={item.period}>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">{item.period}</p>
                      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600">
                        {item.actions.map((action) => (
                          <li key={action}>• {action}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 sm:p-5" data-testid="dashboard-disclaimer-card">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-700">Disclaimer</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">{report.results.valuation.disclaimer}</p>
                </div>
              </div>
            </SectionCard>
          </div>
        </>
      )}
    </motion.div>
  );
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
}

function AppContent() {
  const [meta, setMeta] = useState(null);
  const [appState, setAppState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const metaResponse = await fetchMeta();
        setMeta(metaResponse);

        const defaults = {
          client: {
            client_name: "",
            client_email: "",
            client_phone: "",
            business_name: "",
            planned_exit_years: 3,
          },
          assessment: buildDefaultAssessment(metaResponse.readiness_categories),
          valuation: buildDefaultValuation(metaResponse),
          report: null,
        };

        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setAppState(defaults);
          return;
        }

        const parsed = JSON.parse(stored);
        setAppState({
          client: { ...defaults.client, ...(parsed.client || {}) },
          assessment: parsed.assessment?.categories?.length ? parsed.assessment : defaults.assessment,
          valuation: {
            ...defaults.valuation,
            ...(parsed.valuation || {}),
            multiple_overrides: {
              ...defaults.valuation.multiple_overrides,
              ...(parsed.valuation?.multiple_overrides || {}),
            },
            qualitative_scores: {
              ...defaults.valuation.qualitative_scores,
              ...(parsed.valuation?.qualitative_scores || {}),
            },
          },
          report: parsed.report || null,
        });
      } catch (error) {
        toast.error("Unable to load the dashboard setup.");
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (appState) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appState));
    }
  }, [appState]);

  const updateState = (updater) => {
    setAppState((previous) => {
      const next = updater(previous);
      return { ...next, report: null };
    });
  };

  const updateClient = (field, value) => {
    updateState((previous) => ({
      ...previous,
      client: { ...previous.client, [field]: value },
    }));
  };

  const updateWeight = (categoryIndex, value) => {
    updateState((previous) => {
      const categories = previous.assessment.categories.map((category, index) =>
        index === categoryIndex ? { ...category, weight: value } : category
      );
      return { ...previous, assessment: { ...previous.assessment, categories } };
    });
  };

  const updateQuestionScore = (categoryIndex, questionIndex, value) => {
    updateState((previous) => {
      const categories = previous.assessment.categories.map((category, categoryPointer) => {
        if (categoryPointer !== categoryIndex) {
          return category;
        }
        return {
          ...category,
          questions: category.questions.map((question, questionPointer) =>
            questionPointer === questionIndex ? { ...question, score: value } : question
          ),
        };
      });
      return { ...previous, assessment: { ...previous.assessment, categories } };
    });
  };

  const updateQuestionExplanation = (categoryIndex, questionIndex, value) => {
    updateState((previous) => {
      const categories = previous.assessment.categories.map((category, categoryPointer) => {
        if (categoryPointer !== categoryIndex) {
          return category;
        }
        return {
          ...category,
          questions: category.questions.map((question, questionPointer) =>
            questionPointer === questionIndex ? { ...question, explanation: value } : question
          ),
        };
      });
      return { ...previous, assessment: { ...previous.assessment, categories } };
    });
  };

  const updateValuationField = (field, value) => {
    updateState((previous) => ({
      ...previous,
      valuation: { ...previous.valuation, [field]: value },
    }));
  };

  const updateOverride = (field, value) => {
    updateState((previous) => ({
      ...previous,
      valuation: {
        ...previous.valuation,
        multiple_overrides: { ...previous.valuation.multiple_overrides, [field]: value },
      },
    }));
  };

  const updateQualitativeScore = (field, value) => {
    updateState((previous) => ({
      ...previous,
      valuation: {
        ...previous.valuation,
        qualitative_scores: { ...previous.valuation.qualitative_scores, [field]: value },
      },
    }));
  };

  const reportPayload = useMemo(() => (appState ? sanitisePayload(appState) : null), [appState]);

  const generateReport = async () => {
    if (!appState || !isClientComplete(appState.client)) {
      toast.error("Please complete the client details before generating the dashboard.");
      return;
    }
    if (!reportPayload.valuation.gross_revenue || !reportPayload.valuation.sde) {
      toast.error("Please complete revenue and SDE to build the valuation range.");
      return;
    }

    setGenerating(true);
    try {
      const response = await analyzeReport(reportPayload);
      setAppState((previous) => ({ ...previous, report: response }));
      toast.success("Dashboard generated successfully.");
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to generate the dashboard right now.");
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const sendEmailFlow = async () => {
    if (!appState?.report?.report_id) {
      toast.error("Generate the dashboard before sending the PDF.");
      return;
    }
    setEmailing(true);
    try {
      const response = await emailReport(appState.report.report_id, appState.client.client_email);
      if (response.sent) {
        toast.success(response.message);
      } else {
        toast.message(response.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to send the report email.");
    } finally {
      setEmailing(false);
    }
  };

  if (loading || !meta || !appState) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6" data-testid="loading-screen">
        <div className="space-y-3 text-center">
          <img alt="CARE Outcomes" className="mx-auto h-12 w-auto" src={meta?.brand?.logo_url || "https://images.squarespace-cdn.com/content/v1/642b858923d4144d506f10c1/604b8893-9b6e-400f-bfa0-8a7c20afe363/2025-10-27_CARE+Outcomes+Logo+Gold-01.png?format=1500w"} />
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">Loading dashboard</p>
          <p className="text-base text-slate-600">Preparing the CARE Outcomes client experience…</p>
        </div>
      </div>
    );
  }

  return (
    <Shell client={appState.client} meta={meta}>
      <Routes>
        <Route path="/" element={<LandingPage client={appState.client} meta={meta} updateClient={updateClient} />} />
        <Route
          path="/assessment"
          element={
            <AssessmentPage
              assessment={appState.assessment}
              bands={meta.readiness_bands}
              updateQuestionExplanation={updateQuestionExplanation}
              updateQuestionScore={updateQuestionScore}
              updateWeight={updateWeight}
            />
          }
        />
        <Route
          path="/valuation"
          element={
            <ValuationPage
              meta={meta}
              updateOverride={updateOverride}
              updateQualitativeScore={updateQualitativeScore}
              updateValuationField={updateValuationField}
              valuation={appState.valuation}
            />
          }
        />
        <Route
          path="/dashboard"
          element={
            <DashboardPage
              client={appState.client}
              emailing={emailing}
              generateReport={generateReport}
              generating={generating}
              meta={meta}
              report={appState.report}
              sendEmailFlow={sendEmailFlow}
            />
          }
        />
      </Routes>
    </Shell>
  );
}

function App() {
  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
